import { useEffect, useRef, useState } from "react";
import { Image, Typography } from "antd";

interface PetConfig {
  pet_avatar?: string;
  pet_name?: string;
  pet_type?: string;
  breed?: string;
  gender?: string;
  birthday?: string;
  cover_image?: string;
  bio?: string;
  personality?: string;
  favorite_food?: string;
  favorite_activity?: string;
  weight?: string;
  color?: string;
  owner_name?: string;
  owner_phone?: string;
  owner_address?: string;
  video?: string;
  gallery?: string[];
}

interface Props {
  config: PetConfig;
}

/* ─── Floating Paw Prints ─── */
const FloatingPaws = () => {
  const paws = ["🐾", "🐾", "🐾", "✨", "💛"];
  const [items] = useState(() =>
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 12,
      duration: 10 + Math.random() * 10,
      size: 10 + Math.random() * 12,
      opacity: 0.08 + Math.random() * 0.12,
      char: paws[Math.floor(Math.random() * paws.length)],
      rotate: -30 + Math.random() * 60,
    })),
  );

  return (
    <>
      {items.map((p) => (
        <div
          key={p.id}
          className="fixed pointer-events-none"
          style={{
            left: `${p.left}%`,
            fontSize: `${p.size}px`,
            opacity: p.opacity,
            transform: `rotate(${p.rotate}deg)`,
            animation: `pet-float-up ${p.duration}s linear ${p.delay}s infinite`,
            zIndex: 1,
          }}
        >
          {p.char}
        </div>
      ))}
    </>
  );
};

/* ─── Animated Section ─── */
const AnimatedSection = ({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTimeout(() => setVisible(true), delay);
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        transition: "opacity 0.8s ease, transform 0.8s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(25px) scale(0.98)",
      }}
    >
      {children}
    </div>
  );
};

/* ─── Info Row ─── */
const InfoRow = ({ icon, label, value }: { icon: string; label: string; value: string }) => (
  <div className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
    <span style={{ fontSize: "1.1rem", width: 28, textAlign: "center" }}>{icon}</span>
    <div className="flex-1">
      <span
        style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: "0.65rem",
          letterSpacing: "0.1em",
          color: "rgba(0,0,0,0.35)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <p
        style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: "0.95rem",
          color: "#3d2c2c",
          margin: 0,
          fontWeight: 600,
        }}
      >
        {value}
      </p>
    </div>
  </div>
);

/* ─── Paw Divider ─── */
const PawDivider = () => (
  <div className="flex items-center justify-center gap-2 py-8">
    <div
      className="h-px w-12"
      style={{ background: "linear-gradient(to right, transparent, rgba(210,160,110,0.3))" }}
    />
    <span style={{ fontSize: "0.9rem", opacity: 0.4, animation: "pet-bounce 2s ease-in-out infinite" }}>🐾</span>
    <div className="h-px w-8" style={{ background: "rgba(210,160,110,0.25)" }} />
    <span style={{ fontSize: "0.7rem", opacity: 0.3, animation: "pet-bounce 2s ease-in-out 0.3s infinite" }}>🐾</span>
    <div className="h-px w-8" style={{ background: "rgba(210,160,110,0.25)" }} />
    <span style={{ fontSize: "0.9rem", opacity: 0.4, animation: "pet-bounce 2s ease-in-out 0.6s infinite" }}>🐾</span>
    <div className="h-px w-12" style={{ background: "linear-gradient(to left, transparent, rgba(210,160,110,0.3))" }} />
  </div>
);

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const PublicPet = ({ config }: Props) => {
  const petEmoji = (() => {
    const t = (config.pet_type || "").toLowerCase();
    if (t.includes("mèo") || t.includes("cat")) return "🐱";
    if (t.includes("chó") || t.includes("dog")) return "🐶";
    if (t.includes("hamster") || t.includes("chuột")) return "🐹";
    if (t.includes("thỏ") || t.includes("rabbit")) return "🐰";
    if (t.includes("chim") || t.includes("bird")) return "🐦";
    if (t.includes("cá") || t.includes("fish")) return "🐟";
    return "🐾";
  })();

  const infoItems = [
    { icon: petEmoji, label: "Loại", value: config.pet_type },
    { icon: "🏷️", label: "Giống", value: config.breed },
    {
      icon:
        config.gender?.toLowerCase().includes("cái") || config.gender?.toLowerCase().includes("female") ? "♀️" : "♂️",
      label: "Giới tính",
      value: config.gender,
    },
    { icon: "🎂", label: "Sinh nhật / Tuổi", value: config.birthday },
    { icon: "⚖️", label: "Cân nặng", value: config.weight },
    { icon: "🎨", label: "Màu lông", value: config.color },
  ].filter((item) => item.value);

  const traitItems = [
    { icon: "💖", label: "Tính cách", value: config.personality },
    { icon: "🍗", label: "Món yêu thích", value: config.favorite_food },
    { icon: "⚽", label: "Hoạt động thích nhất", value: config.favorite_activity },
  ].filter((item) => item.value);

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: "#fdf6ef" }}>
      {/* Global Pet Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,300;0,400;0,600;0,700;0,800&family=Baloo+2:wght@400;600;700;800&family=Quicksand:wght@300;400;500;600;700&display=swap');

        @keyframes pet-float-up {
          0% { transform: translateY(110vh) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-10vh) rotate(20deg); opacity: 0; }
        }
        @keyframes pet-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes pet-wiggle {
          0%, 100% { transform: rotate(-3deg); }
          50% { transform: rotate(3deg); }
        }
        @keyframes pet-breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }
        @keyframes pet-wag {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(15deg); }
          75% { transform: rotate(-15deg); }
        }
        @keyframes pet-sparkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }
        @keyframes pet-scroll-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(6px); }
        }
        @keyframes pet-avatar-ring {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .pet-card {
          background: #fff;
          border: 1px solid rgba(210,160,110,0.12);
          box-shadow: 0 2px 12px rgba(180,130,80,0.06), 0 8px 30px rgba(180,130,80,0.04);
          border-radius: 24px;
        }

        .pet-card-warm {
          background: linear-gradient(135deg, #fff9f2 0%, #fff5eb 100%);
          border: 1px solid rgba(210,160,110,0.1);
          box-shadow: 0 2px 12px rgba(180,130,80,0.05);
          border-radius: 24px;
        }

        .pet-cover-overlay {
          background: linear-gradient(180deg,
            rgba(253,246,239,0) 0%,
            rgba(253,246,239,0.3) 40%,
            rgba(253,246,239,0.9) 75%,
            #fdf6ef 100%
          );
        }

        .pet-gallery-img {
          transition: transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94), box-shadow 0.4s ease;
          border-radius: 20px;
        }
        .pet-gallery-img:hover {
          transform: scale(1.03) rotate(-1deg);
          box-shadow: 0 16px 45px rgba(180,130,80,0.15);
        }

        .pet-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 12px;
          border-radius: 50px;
          font-family: 'Nunito', sans-serif;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .pet-sos-card {
          background: linear-gradient(135deg, #fff0f0 0%, #ffe8e8 100%);
          border: 1.5px dashed rgba(230,80,80,0.25);
          border-radius: 20px;
        }
      `}</style>

      <FloatingPaws />

      {/* ══════════ HERO ══════════ */}
      <section className="relative min-h-screen flex flex-col items-center justify-end overflow-hidden pb-8">
        {/* Cover */}
        <div className="absolute inset-0">
          {config.cover_image ? (
            <img src={config.cover_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full"
              style={{
                background: "linear-gradient(160deg, #fce4c4 0%, #f8d4a8 30%, #f0c28c 70%, #e8b878 100%)",
              }}
            />
          )}
          <div className="pet-cover-overlay absolute inset-0" />
        </div>

        {/* Decorative paw trail top */}
        <div className="absolute top-8 left-0 w-full flex justify-center gap-6 opacity-15">
          {["🐾", "🐾", "🐾", "🐾", "🐾"].map((p, i) => (
            <span
              key={i}
              style={{
                fontSize: "1.2rem",
                transform: `rotate(${-20 + i * 10}deg) translateY(${i % 2 === 0 ? 0 : 8}px)`,
              }}
            >
              {p}
            </span>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 w-full max-w-md">
          {/* Pet Avatar */}
          {config.pet_avatar && (
            <div className="relative mx-auto mb-6" style={{ width: 150, height: 150 }}>
              {/* Animated dashed ring */}
              <div
                className="absolute rounded-full"
                style={{
                  inset: -8,
                  border: "2px dashed rgba(210,160,110,0.35)",
                  animation: "pet-avatar-ring 25s linear infinite",
                }}
              />
              {/* Soft glow ring */}
              <div
                className="absolute rounded-full"
                style={{
                  inset: -4,
                  background:
                    "linear-gradient(135deg, rgba(255,200,120,0.4), rgba(255,160,100,0.3), rgba(255,200,150,0.4))",
                  animation: "pet-breathe 4s ease-in-out infinite",
                }}
              />
              <Image
                src={config.pet_avatar}
                alt="pet"
                width={150}
                height={150}
                preview={false}
                className="rounded-full object-cover"
                style={{
                  border: "4px solid #fdf6ef",
                  display: "block",
                  boxShadow: "0 8px 30px rgba(180,130,80,0.2)",
                }}
              />
              {/* Pet emoji badge */}
              <span
                className="absolute text-2xl"
                style={{
                  bottom: 2,
                  right: 2,
                  animation: "pet-wiggle 1.5s ease-in-out infinite",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                }}
              >
                {petEmoji}
              </span>
              {/* Sparkles */}
              {[
                { top: -4, right: 10, delay: "0s" },
                { top: 20, left: -8, delay: "0.8s" },
                { bottom: 15, right: -6, delay: "1.5s" },
              ].map((pos, i) => (
                <span
                  key={i}
                  className="absolute"
                  style={{
                    ...pos,
                    fontSize: "0.7rem",
                    animation: `pet-sparkle 2.5s ease-in-out ${pos.delay} infinite`,
                  }}
                >
                  ✨
                </span>
              ))}
            </div>
          )}

          {/* Pet Name */}
          {config.pet_name && (
            <h1
              style={{
                fontFamily: "'Baloo 2', cursive",
                fontSize: "clamp(2.2rem, 9vw, 3rem)",
                color: "#3d2c2c",
                lineHeight: 1.1,
                marginBottom: 6,
              }}
            >
              {config.pet_name}
            </h1>
          )}

          {/* Tags */}
          <div className="flex items-center justify-center flex-wrap gap-2 mb-2">
            {config.pet_type && (
              <span className="pet-tag" style={{ background: "rgba(255,180,100,0.15)", color: "#b87333" }}>
                {petEmoji} {config.pet_type}
              </span>
            )}
            {config.breed && (
              <span className="pet-tag" style={{ background: "rgba(160,200,130,0.15)", color: "#6b8e50" }}>
                🏷️ {config.breed}
              </span>
            )}
            {config.gender && (
              <span className="pet-tag" style={{ background: "rgba(180,160,220,0.15)", color: "#7b68ae" }}>
                {config.gender?.toLowerCase().includes("cái") || config.gender?.toLowerCase().includes("female")
                  ? "♀️"
                  : "♂️"}{" "}
                {config.gender}
              </span>
            )}
          </div>

          {/* Scroll hint */}
          <div className="mt-12 flex flex-col items-center gap-1">
            <span
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: "0.65rem",
                letterSpacing: "0.15em",
                color: "rgba(61,44,44,0.3)",
              }}
            >
              Xem thêm về {config.pet_name || "bé"}
            </span>
            <span style={{ fontSize: "1.5rem", animation: "pet-scroll-bounce 1.5s ease-in-out infinite" }}>🐾</span>
          </div>
        </div>
      </section>

      {/* ══════════ BIO ══════════ */}
      {config.bio && (
        <section className="relative py-10 px-4">
          <AnimatedSection>
            <div className="pet-card-warm max-w-md mx-auto relative overflow-hidden" style={{ padding: "24px 22px" }}>
              <div className="flex items-center gap-2 mb-3">
                <span style={{ fontSize: "1rem" }}>💬</span>
                <span
                  style={{
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: "0.65rem",
                    letterSpacing: "0.15em",
                    color: "rgba(61,44,44,0.4)",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  Giới thiệu
                </span>
              </div>

              <Typography.Paragraph
                style={{
                  fontFamily: "'Quicksand', sans-serif",
                  fontSize: "1rem",
                  lineHeight: 1.8,
                  color: "rgba(61,44,44,0.75)",
                  marginBottom: 0,
                }}
              >
                {config.bio}
              </Typography.Paragraph>
            </div>
          </AnimatedSection>
        </section>
      )}

      <PawDivider />

      {/* ══════════ PET ID CARD ══════════ */}
      {infoItems.length > 0 && (
        <section className="relative py-6 px-4">
          <AnimatedSection className="text-center mb-6">
            <span
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: "0.65rem",
                letterSpacing: "0.2em",
                color: "rgba(61,44,44,0.35)",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              Hồ sơ
            </span>
            <Typography.Title
              level={3}
              style={{
                color: "#3d2c2c",
                marginTop: 4,
                fontFamily: "'Baloo 2', cursive",
              }}
            >
              Thẻ Thú Cưng 🪪
            </Typography.Title>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="pet-card max-w-md mx-auto relative overflow-hidden" style={{ padding: "20px 22px" }}>
              {/* Top color strip */}
              <div
                className="absolute top-0 left-0 w-full"
                style={{
                  height: 4,
                  background: "linear-gradient(to right, #f0a060, #e88040, #f0a060)",
                  borderRadius: "24px 24px 0 0",
                }}
              />

              {/* Mini avatar + name header */}
              <div className="flex items-center gap-3 mb-4 pb-3" style={{ borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
                {config.pet_avatar && (
                  <img
                    src={config.pet_avatar}
                    alt=""
                    className="rounded-full object-cover"
                    style={{
                      width: 44,
                      height: 44,
                      border: "2px solid rgba(210,160,110,0.2)",
                    }}
                  />
                )}
                <div>
                  <p
                    style={{
                      fontFamily: "'Baloo 2', cursive",
                      fontSize: "1.1rem",
                      color: "#3d2c2c",
                      margin: 0,
                      lineHeight: 1.2,
                    }}
                  >
                    {config.pet_name}
                  </p>
                  {config.breed && (
                    <span
                      style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: "0.75rem",
                        color: "rgba(61,44,44,0.4)",
                      }}
                    >
                      {config.breed}
                    </span>
                  )}
                </div>
                <span className="ml-auto text-2xl" style={{ animation: "pet-wag 0.8s ease-in-out infinite" }}>
                  {petEmoji}
                </span>
              </div>

              {/* Info rows */}
              {infoItems.map((item, i) => (
                <InfoRow key={i} icon={item.icon} label={item.label} value={item.value!} />
              ))}
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ PERSONALITY & FAVORITES ══════════ */}
      {traitItems.length > 0 && (
        <section className="relative py-8 px-4">
          <PawDivider />

          <AnimatedSection className="text-center mb-6">
            <Typography.Title level={3} style={{ color: "#3d2c2c", fontFamily: "'Baloo 2', cursive" }}>
              Bé Thích Gì? 💛
            </Typography.Title>
          </AnimatedSection>

          <div className="max-w-md mx-auto flex flex-col gap-3">
            {traitItems.map((item, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="pet-card flex items-start gap-4" style={{ padding: "18px 20px" }}>
                  <div
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 14,
                      background: "rgba(255,200,120,0.15)",
                    }}
                  >
                    <span style={{ fontSize: "1.2rem" }}>{item.icon}</span>
                  </div>
                  <div>
                    <span
                      style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: "0.65rem",
                        letterSpacing: "0.12em",
                        color: "rgba(61,44,44,0.4)",
                        textTransform: "uppercase",
                        fontWeight: 700,
                      }}
                    >
                      {item.label}
                    </span>
                    <p
                      style={{
                        fontFamily: "'Quicksand', sans-serif",
                        fontSize: "0.95rem",
                        color: "#3d2c2c",
                        margin: "2px 0 0 0",
                        fontWeight: 600,
                      }}
                    >
                      {item.value}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      )}

      {/* ══════════ SOS — OWNER CONTACT ══════════ */}
      {(config.owner_name || config.owner_phone) && (
        <section className="relative py-8 px-4">
          <PawDivider />

          <AnimatedSection>
            <div className="pet-sos-card max-w-md mx-auto relative overflow-hidden" style={{ padding: "22px 20px" }}>
              <div className="flex items-center gap-2 mb-4">
                <span style={{ fontSize: "1.1rem" }}>🆘</span>
                <span
                  style={{
                    fontFamily: "'Baloo 2', cursive",
                    fontSize: "1rem",
                    color: "#c0392b",
                  }}
                >
                  Nếu bé bị lạc, xin liên hệ
                </span>
              </div>

              {config.owner_name && (
                <div className="flex items-center gap-3 mb-3">
                  <span style={{ fontSize: "0.9rem" }}>👤</span>
                  <div>
                    <span
                      style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: "0.6rem",
                        color: "rgba(0,0,0,0.35)",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      Chủ nhân
                    </span>
                    <p
                      style={{
                        fontFamily: "'Quicksand', sans-serif",
                        fontSize: "0.95rem",
                        color: "#3d2c2c",
                        margin: 0,
                        fontWeight: 600,
                      }}
                    >
                      {config.owner_name}
                    </p>
                  </div>
                </div>
              )}

              {config.owner_phone && (
                <div
                  className="flex items-center gap-3 mb-3 cursor-pointer"
                  onClick={() => window.open(`tel:${config.owner_phone}`)}
                  style={{
                    background: "rgba(230,80,80,0.08)",
                    borderRadius: 14,
                    padding: "10px 14px",
                    transition: "all 0.3s ease",
                  }}
                >
                  <span style={{ fontSize: "1.1rem" }}>📞</span>
                  <div>
                    <span
                      style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: "0.6rem",
                        color: "rgba(0,0,0,0.35)",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      Số điện thoại
                    </span>
                    <p
                      style={{
                        fontFamily: "'Quicksand', sans-serif",
                        fontSize: "1.05rem",
                        color: "#c0392b",
                        margin: 0,
                        fontWeight: 700,
                      }}
                    >
                      {config.owner_phone}
                    </p>
                  </div>
                  <span className="ml-auto" style={{ fontSize: "0.8rem", color: "#c0392b" }}>
                    Gọi →
                  </span>
                </div>
              )}

              {config.owner_address && (
                <div className="flex items-center gap-3">
                  <span style={{ fontSize: "0.9rem" }}>📍</span>
                  <div>
                    <span
                      style={{
                        fontFamily: "'Nunito', sans-serif",
                        fontSize: "0.6rem",
                        color: "rgba(0,0,0,0.35)",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      Địa chỉ
                    </span>
                    <p
                      style={{
                        fontFamily: "'Quicksand', sans-serif",
                        fontSize: "0.9rem",
                        color: "#3d2c2c",
                        margin: 0,
                        fontWeight: 500,
                      }}
                    >
                      {config.owner_address}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ VIDEO ══════════ */}
      {config.video && (
        <section className="relative py-10 px-4">
          <PawDivider />
          <AnimatedSection>
            <div className="text-center mb-5">
              <Typography.Title level={4} style={{ color: "#3d2c2c", fontFamily: "'Baloo 2', cursive" }}>
                Video Đáng Yêu 🎬
              </Typography.Title>
            </div>
            <div className="pet-card overflow-hidden max-w-md mx-auto" style={{ padding: 6 }}>
              <video
                src={config.video}
                controls
                playsInline
                className="w-full"
                style={{ aspectRatio: "16/9", objectFit: "cover", borderRadius: 20 }}
                poster={config.cover_image}
              />
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ GALLERY ══════════ */}
      {config.gallery && config.gallery.length > 0 && (
        <section className="relative py-12 px-4">
          <PawDivider />

          <AnimatedSection className="text-center mb-8">
            <span
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: "0.65rem",
                letterSpacing: "0.2em",
                color: "rgba(61,44,44,0.35)",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              Album
            </span>
            <Typography.Title level={3} style={{ color: "#3d2c2c", marginTop: 4, fontFamily: "'Baloo 2', cursive" }}>
              Khoảnh Khắc Của {config.pet_name || "Bé"} 📸
            </Typography.Title>
          </AnimatedSection>

          <div className="max-w-md mx-auto columns-2 gap-3" style={{ columnGap: 12 }}>
            {config.gallery.map((img, i) => {
              const rotations = [-1.5, 1, -0.5, 1.5, -1, 0.8];
              const rot = rotations[i % rotations.length];

              return (
                <AnimatedSection key={i} delay={i * 100} className="mb-3 break-inside-avoid">
                  <div
                    className="pet-gallery-img overflow-hidden relative"
                    style={{
                      border: "3px solid #fff",
                      boxShadow: "0 4px 16px rgba(180,130,80,0.08)",
                      transform: `rotate(${rot}deg)`,
                    }}
                  >
                    <Image
                      src={img}
                      alt={`pet-${i}`}
                      className="w-full object-cover"
                      style={{
                        aspectRatio: i % 3 === 0 ? "3/4" : i % 3 === 1 ? "1/1" : "4/5",
                        display: "block",
                      }}
                    />
                    {/* Cute sticker overlay */}
                    {i % 4 === 0 && (
                      <span
                        className="absolute"
                        style={{
                          bottom: 6,
                          right: 8,
                          fontSize: "1.3rem",
                          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))",
                        }}
                      >
                        💛
                      </span>
                    )}
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </section>
      )}

      {/* ══════════ CLOSING ══════════ */}
      <section className="relative py-16 px-4">
        <AnimatedSection className="text-center">
          <div className="mb-5 flex items-center justify-center gap-3">
            <span className="text-3xl" style={{ animation: "pet-bounce 2s ease-in-out infinite" }}>
              🐾
            </span>
            <span className="text-4xl" style={{ animation: "pet-bounce 2s ease-in-out 0.2s infinite" }}>
              💛
            </span>
            <span className="text-3xl" style={{ animation: "pet-bounce 2s ease-in-out 0.4s infinite" }}>
              🐾
            </span>
          </div>

          <h3
            style={{
              fontFamily: "'Baloo 2', cursive",
              fontSize: "clamp(1.6rem, 6vw, 2.2rem)",
              color: "#3d2c2c",
              marginBottom: 8,
            }}
          >
            {config.pet_name || "Bé"} Yêu Bạn! 🥰
          </h3>

          <p
            className="max-w-xs mx-auto"
            style={{
              fontFamily: "'Quicksand', sans-serif",
              fontSize: "0.9rem",
              color: "rgba(61,44,44,0.4)",
              lineHeight: 1.7,
            }}
          >
            Cảm ơn bạn đã ghé thăm trang của {config.pet_name || "bé"}. Hãy yêu thương và bảo vệ thú cưng nhé!
          </p>
        </AnimatedSection>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="relative py-6 px-4 text-center" style={{ borderTop: "1px solid rgba(210,160,110,0.1)" }}>
        <div className="flex items-center justify-center gap-1 mb-2">
          {["🐾", "💛", "🐾"].map((e, i) => (
            <span key={i} className="text-xs" style={{ opacity: 0.3 }}>
              {e}
            </span>
          ))}
        </div>
        <Typography.Text
          style={{
            fontFamily: "'Nunito', sans-serif",
            color: "rgba(61,44,44,0.2)",
            fontSize: "0.7rem",
          }}
        >
          Powered by Miraway
        </Typography.Text>
      </footer>
    </div>
  );
};

export default PublicPet;
